import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { IMortgage } from '../../../../../../Interfaces/IMortgage';
import { NumericFormat } from 'react-number-format';

interface IProps {
  handleBack: Function;
  handleNext: Function;
  mortgageObject: IMortgage;
  setMortgageObject: Function;
}

const transcriptOptions = [
  'simple_transcription',
  'certified_copy',
  'simple_execution',
  'enforceable_copy',
] as const;

type TranscriptOption = (typeof transcriptOptions)[number];

export const MortgageGeneral: React.FC<IProps> = ({
  handleBack,
  handleNext,
  mortgageObject,
  setMortgageObject,
}) => {
  const formRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const initialCheckedOptions: Record<TranscriptOption, boolean> = {
    simple_transcription:
      mortgageObject.Transcripts?.includes('simple_transcription') || false,
    certified_copy:
      mortgageObject.Transcripts?.includes('certified_copy') || false,
    simple_execution:
      mortgageObject.Transcripts?.includes('simple_execution') || false,
    enforceable_copy:
      mortgageObject.Transcripts?.includes('enforceable_copy') || false,
  };

  const [checkedOptions, setCheckedOptions] = useState(initialCheckedOptions);

  const [propertyBuy, setPropertyBuy] = useState<boolean | null>(
    mortgageObject.PropertyForBuying === false ||
      mortgageObject.PropertyForBuying === true
      ? mortgageObject.PropertyForBuying
      : null
  );
  const [propertyOwned, setPropertyOwned] = useState<boolean | null>(
    mortgageObject.PropertyOwned === true ||
      mortgageObject.PropertyOwned === false
      ? mortgageObject.PropertyOwned
      : null
  );

  useEffect(() => {
    setMortgageObject({
      ...mortgageObject,
      PropertyForBuying: propertyBuy,
      PropertyOwned: propertyOwned,
    });
  }, [propertyBuy, propertyOwned]);

  const handleBeneficiaryTypeEditChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setMortgageObject({
      ...mortgageObject,
      BeneficiaryType: event.target.value,
    });
  };

  const handlePropertyBuyChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.value === 'yes') {
      setPropertyBuy(true);
    } else {
      setPropertyBuy(false);
      setPropertyOwned(null);
      setMortgageObject({
        ...mortgageObject,
        DocumentIndexNumber: '',
        OperationNumber: '',
        PropertyOwned: null,
      });
    }
  };

  const handlePropertyOwnedChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.value === 'no') {
      setMortgageObject({
        ...mortgageObject,
        DocumentIndexNumber: '',
        OperationNumber: '',
        PropertyOwned: false,
      });
      setPropertyOwned(false);
    } else if (event.target.value === 'yes') {
      setPropertyOwned(true);
    }
  };
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCheckedOptions({
      ...checkedOptions,
      [event.target.name as TranscriptOption]: event.target.checked,
    });
  };

  const handleSubmit = () => {
    handleNext();
  };
  const isFormValid = () => {
    if (formRef.current && formRef.current.checkValidity()) {
      return true;
    } else {
      return false;
    }
  };
  const handleOnSave = () => {
    if (formRef.current && formRef.current.checkValidity()) {
      handleSubmit();
    } else if (formRef.current && !formRef.current.checkValidity()) {
      const elements = Array.from(formRef.current.elements).reverse();

      elements.forEach((element: any) => {
        if (element.reportValidity()) {
          element.reportValidity();
        }
      });
    }
  };

  useEffect(() => {
    const selectedTranscripts = transcriptOptions.filter(
      option => checkedOptions[option]
    );

    setMortgageObject({
      ...mortgageObject,

      Transcripts: selectedTranscripts,
    });
  }, [checkedOptions]);

  return (
    <>
      <form ref={formRef}>
        <Typography mt={6} variant='h4'>
          Grundpfand
        </Typography>
        <Box display='flex' flexDirection='column'>
          <Typography sx={{ mb: 2, mt: 6 }}>
            Für wen soll die Grundschuld bestellt werden?
          </Typography>
          <FormControl component='fieldset'>
            <RadioGroup
              aria-label='for-who'
              name='who'
              value={mortgageObject.BeneficiaryType}
              onChange={handleBeneficiaryTypeEditChange}
            >
              <FormControlLabel
                value='institution'
                control={<Radio required />}
                label='Eine Bank (auch [Bau-] Sparkasse) oder eine Versicherung'
              />
              <FormControlLabel
                value='private'
                control={<Radio required />}
                label='Eine Privatperson oder ein sonstiges Unternehmen'
              />
            </RadioGroup>
          </FormControl>
        </Box>

        <Box sx={{ mt: 6 }} />
        <Typography sx={{ mb: 2 }}>
          Wird die Grundschuld zur Finanzierung eines Immobilienkaufs benötigt?
        </Typography>
        <FormControl component='fieldset'>
          <RadioGroup
            row
            aria-label='property'
            name='property-buy'
            value={propertyBuy === null ? '' : propertyBuy ? 'yes' : 'no'}
            onChange={handlePropertyBuyChange}
          >
            <FormControlLabel
              value='yes'
              control={<Radio required />}
              label='Ja'
            />
            <FormControlLabel
              value='no'
              control={<Radio required />}
              label='Nein'
            />
          </RadioGroup>
        </FormControl>
        {propertyBuy && (
          <>
            <Typography mt={6} mb={2}>
              Ist der Immobilienkauf bereits erfolgt?
            </Typography>
            <FormControl component='fieldset'>
              <RadioGroup
                row
                aria-label='property-owned'
                name='property-owned'
                value={
                  propertyOwned === null ? '' : propertyOwned ? 'yes' : 'no'
                }
                onChange={handlePropertyOwnedChange}
              >
                <FormControlLabel
                  value='yes'
                  control={<Radio required />}
                  label='Ja'
                />
                <FormControlLabel
                  value='no'
                  control={<Radio required />}
                  label='Nein'
                />
              </RadioGroup>
            </FormControl>
            {propertyOwned && (
              <>
                <Box sx={{ mt: 6 }} />
                <Typography sx={{ mb: 2 }}>
                  Geben Sie bitte die Urkundenverzeichnisnummer ein (sog.
                  UVZ-Nummer).
                </Typography>
                <Grid item xs={6} sm={4}>
                  <Grid item xs={6} sm={4}>
                    <TextField
                      label='Urkundenverzeichnisnummer'
                      size='small'
                      fullWidth
                      value={mortgageObject.DocumentIndexNumber}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        setMortgageObject({
                          ...mortgageObject,
                          DocumentIndexNumber: event.target.value,
                        });
                      }}
                      required
                    />
                  </Grid>
                </Grid>
                <Box sx={{ mt: 6 }} />
                <Typography sx={{ mb: 2 }}>
                  Geben Sie die Vorgangsnummer (sechsstellig, befindet sich
                  ausgegraut auf der ersten Seite des Entwurfs) an.
                </Typography>
                <Grid item xs={6} sm={4}>
                  <TextField
                    label='Vorgangsnummer'
                    size='small'
                    fullWidth
                    value={mortgageObject.OperationNumber}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setMortgageObject({
                        ...mortgageObject,
                        OperationNumber: event.target.value,
                      });
                    }}
                    required
                  />
                </Grid>
              </>
            )}
          </>
        )}

        <Typography mt={6} mb={2}>
          Über welchen Betrag soll die Grundschuld bestellt werden?
        </Typography>
        <Grid item xs={6} sm={4}>
          <NumericFormat
            label='Betrag der Grundschuld'
            size='small'
            suffix='€'
            fixedDecimalScale
            decimalScale={2}
            thousandsGroupStyle='thousand'
            thousandSeparator='.'
            decimalSeparator=','
            fullWidth
            required
            value={mortgageObject.AmountMortgage}
            customInput={TextField}
            inputRef={inputRef}
            onValueChange={values => {
              const { floatValue } = values;
              const cursorPosition = inputRef.current?.selectionStart;
              setMortgageObject({
                ...mortgageObject,
                AmountMortgage: floatValue !== undefined ? floatValue : null,
              });

              setTimeout(() => {
                if (inputRef.current && cursorPosition) {
                  inputRef.current.setSelectionRange(
                    cursorPosition,
                    cursorPosition
                  );
                }
              }, 0);
            }}
            allowNegative={false}
          />
        </Grid>

        <Box sx={{ mt: 6 }} />
        <Typography sx={{ mb: 2 }}>
          Wie hoch sind die sog. Grundschuldzinsen (in %)?
        </Typography>
        <Grid item xs={6} sm={4}>
          <NumericFormat
            value={mortgageObject.MortgageInterest}
            suffix='%'
            decimalSeparator='.'
            label='Grundschuldzinsen'
            size='small'
            fullWidth
            required
            customInput={TextField}
            decimalScale={2}
            fixedDecimalScale={true}
            allowNegative={false}
            isAllowed={values => {
              const { floatValue } = values;

              if (floatValue && floatValue >= 100) {
                return false;
              }
              return true;
            }}
            onValueChange={values => {
              const { floatValue } = values;
              setMortgageObject({
                ...mortgageObject,
                MortgageInterest: floatValue !== undefined ? floatValue : null,
              });
            }}
          />
        </Grid>

        <Box sx={{ mt: 6 }} />
        <Typography sx={{ mb: 2 }}>Gibt es Nebenleistungen?</Typography>
        <Grid item xs={6} sm={4}>
          <TextField
            label='Nebenleistungen'
            size='small'
            fullWidth
            rows={3}
            multiline
            value={mortgageObject.AdditionalServices}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setMortgageObject({
                ...mortgageObject,
                AdditionalServices: event.target.value,
              })
            }
          />
        </Grid>
        <Box sx={{ mt: 6 }} />

        <Box sx={{ mt: 6 }}>
          <Typography sx={{ mb: 2 }}>
            Welche Abschriften soll der Gläubiger erhalten?
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={checkedOptions.simple_transcription}
                  onChange={handleChange}
                  name='simple_transcription'
                />
              }
              label='Einfache Abschrift'
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={checkedOptions.certified_copy}
                  onChange={handleChange}
                  name='certified_copy'
                />
              }
              label='Beglaubigte Abschrift'
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={checkedOptions.simple_execution}
                  onChange={handleChange}
                  name='simple_execution'
                />
              }
              label='Einfache Ausfertigung'
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={checkedOptions.enforceable_copy}
                  onChange={handleChange}
                  name='enforceable_copy'
                />
              }
              label='Vollstreckbare Ausfertigung'
            />
          </FormGroup>
        </Box>

        <Box mt={6} sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
          <Button
            variant='outlined'
            onClick={() => handleBack()}
            sx={{ mr: 1 }}
          >
            Zurück
          </Button>
          <Box sx={{ flex: '1 1 auto' }} />
          <Button
            onClick={() => {
              handleOnSave();
            }}
            variant={isFormValid() ? 'contained' : 'outlined'}
          >
            Weiter
          </Button>
        </Box>
      </form>
    </>
  );
};
